import type { LicenseQuestionAnswerType } from "@/hera-api/client/types.gen";

/** Answer types with no useful textual value for licenses-list columns.
 * Live-link answers are plain text values and may be shown in the list.
 */
const NON_LISTABLE_ANSWER_TYPES = new Set<string>([
  "information",
  "end",
  "jump",
  "document",
  "photo",
  "branch_on_choice",
]);

/** True when the answer can be shown as a licenses-list text column. */
export function isListableQuestionAnswerType(
  t: LicenseQuestionAnswerType | string,
): boolean {
  return !NON_LISTABLE_ANSWER_TYPES.has(t);
}

/** Drop info/file/flow questions from licenses-list column pickers. */
export function filterListableQuestionColumns<
  T extends { answer_type?: LicenseQuestionAnswerType | string | null },
>(columns: readonly T[]): T[] {
  return columns.filter((column) =>
    column.answer_type == null
      ? true
      : isListableQuestionAnswerType(column.answer_type),
  );
}

function registrantQuestionColumnGroup(column: {
  tag?: string | null;
  default_visible?: boolean;
}): number {
  const tag = column.tag?.toUpperCase();
  if (tag === "OMT") return 0;
  if (tag === "AMS") return 1;
  return column.default_visible ? 2 : 3;
}

/** Listable registrant columns with OMT, then AMS, then other visible company columns. */
export function listableRegistrantQuestionColumns<
  T extends {
    answer_type?: LicenseQuestionAnswerType | string | null;
    tag?: string | null;
    default_visible?: boolean;
  },
>(columns: readonly T[]): T[] {
  return [...filterListableQuestionColumns(columns)].sort(
    (left, right) =>
      registrantQuestionColumnGroup(left) -
      registrantQuestionColumnGroup(right),
  );
}

/** Fold older per-field contact flags into the single show-in-list switch. */
export function effectiveShowInList(flags: {
  show_in_list?: boolean | null;
  contact_name_show_in_list?: boolean | null;
  contact_company_name_show_in_list?: boolean | null;
  contact_email_show_in_list?: boolean | null;
  contact_phone_show_in_list?: boolean | null;
  contact_address_show_in_list?: boolean | null;
}): boolean {
  return Boolean(
    flags.show_in_list ||
      flags.contact_name_show_in_list ||
      flags.contact_company_name_show_in_list ||
      flags.contact_email_show_in_list ||
      flags.contact_phone_show_in_list ||
      flags.contact_address_show_in_list,
  );
}

export interface ShowInListFlagValues {
  show_in_list: boolean;
  contact_name_show_in_list: boolean;
  contact_company_name_show_in_list: boolean;
  contact_email_show_in_list: boolean;
  contact_phone_show_in_list: boolean;
  contact_address_show_in_list: boolean;
}

/** Form values for show-in-list. Company questions keep each sub-field. */
export function showInListFormValues(
  answerType: LicenseQuestionAnswerType | string | null | undefined,
  flags: {
    show_in_list?: boolean | null;
    contact_name_show_in_list?: boolean | null;
    contact_company_name_show_in_list?: boolean | null;
    contact_email_show_in_list?: boolean | null;
    contact_phone_show_in_list?: boolean | null;
    contact_address_show_in_list?: boolean | null;
  },
): ShowInListFlagValues {
  if (answerType === "company_contact") {
    return {
      show_in_list: Boolean(flags.show_in_list),
      contact_name_show_in_list: Boolean(flags.contact_name_show_in_list),
      contact_company_name_show_in_list: false,
      contact_email_show_in_list: Boolean(flags.contact_email_show_in_list),
      contact_phone_show_in_list: Boolean(flags.contact_phone_show_in_list),
      contact_address_show_in_list: Boolean(flags.contact_address_show_in_list),
    };
  }
  return {
    show_in_list: effectiveShowInList(flags),
    contact_name_show_in_list: false,
    contact_company_name_show_in_list: false,
    contact_email_show_in_list: false,
    contact_phone_show_in_list: false,
    contact_address_show_in_list: false,
  };
}

/** Help text for the Show in list switch, based on how the answer is displayed. */
export function showInListHelpText(
  answerType: LicenseQuestionAnswerType | string,
): string {
  if (answerType === "company_contact") {
    return "Show the company name as a column in the licenses list (person name if the company is blank)";
  }
  if (answerType === "name_address_contact") {
    return "Show the person's name as a column in the licenses list";
  }
  if (answerType === "address") {
    return "Show the full address as a column in the licenses list";
  }
  if (answerType === "live_link") {
    return "Show this live link value as a column in the licenses list";
  }
  return "Show this question's answer as a column in the licenses list";
}
